import {
  useContext,
  useState,
  useEffect,
  useMemo
} from "react";

import { AlarmContext } from "../context/AlarmContext";
import { PlantStore } from "../engine/PlantStore";


function LevelTransmitterIndicator({

  tag = "LT-XXXX",

  level = 0,
  totalLevel = 1000,

  inflows = [],
  outflows = [],

  columnX = 0,
  columnY = 0,

  columnWidth = 30,
  columnHeight = 120,

  textX = 0,
  textY = 0,

  showFlow = false,

  highHigh = 0.9,
  lowLow = 0.1,

  backgroundOpacity = 0.6,
  liquidOpacity = 0.8,

  service = "crude",
  inflowTags = [],
  outflowTags = [],

}) {


  /* ================= FLOW NORMALIZER ================= */

 const normalizeFlow = (value) => {

  if (Array.isArray(value)) {

    return value.reduce((sum, item) => {

      if (typeof item === "object" && item !== null) {
        return sum + (Number(item.value) || 0);
      }

      return sum + (Number(item) || 0);

    }, 0);

  }

  if (typeof value === "object" && value !== null) {
    return Number(value.value) || 0;
  }

  return Number(value) || 0;

};



  const totalInflow = useMemo(() => {

    return normalizeFlow(inflows);

  }, [inflows]);



  const totalOutflow = useMemo(() => {

    return normalizeFlow(outflows);

  }, [outflows]);





  /* ================= INITIAL LEVEL + CATCHUP ================= */

const [internalLevel, setInternalLevel] =
  useState(() => {

    const saved =
      Number(
        localStorage.getItem(tag)
      ) ||
      Number(level) ||
      0;

    const lastTime =
      Number(
        localStorage.getItem(
          `${tag}_time`
        )
      );

    if (!lastTime) {

      return Math.max(
        0,
        Math.min(
          totalLevel,
          saved
        )
      );

    }

    const elapsedSeconds =
      (Date.now() - lastTime) / 1000;

    const netFlow =
      Number(totalInflow || 0) -
      Number(totalOutflow || 0);

    /*
      Your runtime engine uses:

      levelChange = netFlow / 60
      every second

      Therefore catchup must use
      exactly the same logic.
    */

    const catchupChange =
      (netFlow / 60) *
      elapsedSeconds;

    const corrected =
      saved +
      catchupChange;

    return Math.max(
      0,
      Math.min(
        totalLevel,
        corrected
      )
    );

});



  /* ================= COLORS ================= */

  const colors = {

    crude: [0, 0, 0],

    water: [30, 144, 255],

    glycol: [50, 205, 50],

    amine: [147, 112, 219],

    condensate: [255, 215, 0],

    diesel: [218, 165, 32],

    naphtha: [255, 255, 137],

    residue: [66, 33, 0],

    kerosene: [237, 125, 49],

    lightnaphtha: [255, 253, 247],

    lpg: [255, 252, 243],

    inhibitor: [29, 255, 29],

  };



  const selectedColor =
    colors[
    service?.toLowerCase()
    ]
    ||
    colors.crude;



  const baseColor =
    `rgba(
${selectedColor[0]},
${selectedColor[1]},
${selectedColor[2]},
${liquidOpacity}
)`;



  const alarmColor =
    `rgba(
255,
0,
0,
${liquidOpacity}
)`;




  /* ================= BLINK ================= */

  const [blink, setBlink] =
    useState(false);




/* ================= SAVE ================= */

useEffect(() => {

  localStorage.setItem(
    tag,
    internalLevel
  );

  localStorage.setItem(
    `${tag}_time`,
    Date.now()
  );

}, [
  tag,
  internalLevel
]);





  /* ================= LEVEL UPDATE ================= */

useEffect(() => {

  const timer =
    setInterval(() => {

      setInternalLevel(prev => {

        const netFlow =
          Number(totalInflow || 0) -
          Number(totalOutflow || 0);

        const levelChange =
          netFlow / 60;

        const next =
          prev + levelChange;

        return Math.max(
          0,
          Math.min(
            totalLevel,
            next
          )
        );

      });

    }, 1000);

  return () =>
    clearInterval(timer);

}, [
  totalInflow,
  totalOutflow,
  totalLevel
]);






  /* ================= SAFE VALUE ================= */

  const safeLevel =
    Number(internalLevel) || 0;




  const percent =
    totalLevel > 0
      ?
      (safeLevel / totalLevel) * 100
      :
      0;


    /* ================= LOW LEVEL INTERLOCK ================= */

const lowLevelInterlock = percent <= 3;



useEffect(() => {

    if (!Array.isArray(outflows)) return;

    const overrides = {
        ...(PlantStore.get().flowOverrides || {})
    };

        outflowTags.forEach(tag => {

        overrides[tag] = percent <= 3;

    });

    PlantStore.set({
        flowOverrides: overrides
    });

}, [percent, outflowTags]);



/* ================= HIGH LEVEL INTERLOCK ================= */

useEffect(() => {

    if (!Array.isArray(inflows)) return;

    const overrides = {
        ...(PlantStore.get().flowOverrides || {})
    };

        inflowTags.forEach(tag => {

        overrides[tag] = percent >= 97;

    });

    PlantStore.set({
        flowOverrides: overrides
    });

}, [percent, inflowTags]);



  /* ================= ALARMS ================= */

  const isHighHigh =
    percent >= highHigh * 100;



  const isLowLow =
    percent <= lowLow * 100;




  useEffect(() => {


    if (isHighHigh || isLowLow) {


      const timer =
        setInterval(() => {


          setBlink(x => !x);


        }, 400);



      return () => clearInterval(timer);


    }


    setBlink(false);


  }, [
    isHighHigh,
    isLowLow
  ]);





  /* ================= ALARM ENGINE ================= */


  const alarm =
    useContext(AlarmContext);



  useEffect(() => {


    if (alarm?.evaluateTag) {


      alarm.evaluateTag(
        tag,
        safeLevel
      );


    }


  }, [
    tag,
    safeLevel,
    alarm
  ]);



  useEffect(() => {

    PlantStore.setInterlock(
        tag,
        lowLevelInterlock
    );

}, [
    tag,
    lowLevelInterlock
]);




  const liquidColor =
    (isHighHigh || isLowLow)
      ?
      (
        blink
          ?
          alarmColor
          :
          baseColor
      )
      :
      baseColor;






  /* ================= LIQUID HEIGHT ================= */


  const fillHeight =
    Math.max(
      0,
      Math.min(
        columnHeight,
        (percent / 100) * columnHeight
      )
    );





  return (

    <>


      {/* ================= COLUMN ================= */}

      <div

        style={{

          position: "absolute",

          left: columnX,

          top: columnY,

          width: columnWidth,

          height: columnHeight,


          border: "2px solid #aaa",

          borderRadius: "6px",

          background:
            `rgba(17,17,17,${backgroundOpacity})`,

          overflow: "hidden",

          boxShadow:
            "inset 0 0 6px rgba(0,0,0,.8)"

        }}

      >



        <div


          style={{

            position: "absolute",

            bottom: 0,

            width: "100%",

            height: fillHeight,

            background: liquidColor,

            transition:
              "height .25s linear"

          }}

        >


          <svg

            width="100%"

            height="20"

            viewBox="0 0 100 20"

            preserveAspectRatio="none"

            style={{

              position: "absolute",

              top: -10,

              left: 0

            }}

          >
            <path

              fill={liquidColor}

              d=
              "M0,10 Q25,0 50,10 T100,10 V20 H0 Z"

            >
              <animate

                attributeName="d"

                dur="1.2s"

                repeatCount="indefinite"


                values="
M0,10 Q25,0 50,10 T100,10 V20 H0 Z;
M0,10 Q25,20 50,10 T100,10 V20 H0 Z;
M0,10 Q25,0 50,10 T100,10 V20 H0 Z
"

              />



            </path>


          </svg>



        </div>



      </div>





      {/* ================= DISPLAY ================= */}


      <div


        style={{

          position: "absolute",

          left: textX,

          top: textY,

          color: "white",

          fontSize: "11px",

          background:
            "rgba(0,0,0,.65)",

          padding: "4px 6px",

          borderRadius: "4px",

          border: "1px solid #444",

          minWidth: "70px"

        }}

      >


        <div

          style={{

            background: "blue",

            padding: "2px"

          }}

        >

          <b>{tag}</b>

        </div>



        <div>

          {safeLevel.toFixed(1)}

        </div>



        <div>

          {percent.toFixed(1)}%

        </div>





        {
          showFlow &&
          <>


            <div style={{ color: "lime" }}>

              IN:
              {totalInflow.toFixed(2)}

            </div>



            <div style={{ color: "orange" }}>

              OUT:
              {totalOutflow.toFixed(2)}

            </div>
          </>
        }

        {
          (isHighHigh || isLowLow)
          &&

          <div

            style={{

              color: "red",

              fontWeight: "bold"

            }}

          >
            {
              isHighHigh
                ?
                "HH LEVEL"
                :
                "LL LEVEL"
            }
          </div>
        }
      </div>
    </>

  );


}


export default LevelTransmitterIndicator;